import { Head } from 'vite-react-ssg';
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import NavigationButtons from "@/components/NavigationButtons";
import { Button } from "@/components/ui/button";
import { WaitlistDialog } from "@/components/WaitlistDialog";
import { ArrowLeft, Clock, Calendar, User, Brain, Zap, Code, Building2, ChevronRight, Lightbulb, Scale, Timer, Chrome } from "lucide-react";
import heroImage from "@/assets/reasoning-era-gemini-deepseek-hero.jpg";

const ReasoningEraGeminiDeepseek = () => {
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "The Reasoning Era Begins: A Deep Dive into Google Gemini 3 and DeepSeek's Quantum Leap",
    "description": "Explore how Google Gemini 3 and DeepSeek are ushering in the era of 'Deep Thinking' AI with revolutionary reasoning capabilities that go beyond pattern matching.",
    "image": "https://deepseek.ai/og-reasoning-era.png",
    "author": {
      "@type": "Organization",
      "name": "The Independent DeepSeek Guide"
    },
    "publisher": {
      "@type": "Organization",
      "name": "The Independent DeepSeek Guide",
      "logo": {
        "@type": "ImageObject",
        "url": "https://deepseek.ai/favicon-ds.png"
      }
    },
    "datePublished": "2025-12-07",
    "dateModified": "2025-12-07",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://deepseek.ai/blog/reasoning-era-gemini-3-deepseek"
    },
    "keywords": ["DeepSeek", "Google Gemini 3", "Reasoning AI", "Deep Thinking", "System 2 AI", "Inference-Time Compute", "AI Models 2025"]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is the difference between System 1 and System 2 AI?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "System 1 AI (traditional LLMs like GPT-4) provides fast, instinctive responses based on pattern matching. System 2 AI (Gemini 3, DeepSeek) uses slow, deliberative reasoning with internal chains of thought, self-checking, and logical verification before delivering answers."
        }
      },
      {
        "@type": "Question",
        "name": "How does DeepSeek compare to Google Gemini 3?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "DeepSeek focuses on efficiency, coding, and mathematics with open-weights accessibility. Google Gemini 3 excels at multimodal reasoning across video/audio with deep enterprise integration. DeepSeek is often 10x cheaper per token while Gemini 3 offers premium enterprise features."
        }
      },
      {
        "@type": "Question",
        "name": "What is Inference-Time Compute?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Inference-Time Compute refers to the computational energy spent thinking about a problem live, rather than relying solely on pre-trained knowledge. This allows AI models to pause, explore different paths, and verify their reasoning before responding."
        }
      },
      {
        "@type": "Question",
        "name": "Can DeepSeek models run on consumer hardware?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, DeepSeek's major achievement is efficiency. They've achieved reasoning capabilities comparable to top-tier proprietary models with a smaller parameter footprint, making it possible to run on high-end consumer hardware or affordable cloud instances."
        }
      }
    ]
  };

  return (
    <>
      <Head>
        <title>Gemini 3 vs DeepSeek — The Reasoning AI Era</title>
        <meta name="description" content="Explore how Google Gemini 3 and DeepSeek move AI from pattern matching toward deeper System 2 reasoning and deliberate problem solving." />
        <meta name="keywords" content="DeepSeek, Google Gemini 3, Reasoning AI, Deep Thinking AI, System 2 AI, Inference-Time Compute, AI comparison, DeepSeek vs Gemini, AI models 2025, multimodal AI, coding AI" />
        <link rel="canonical" href="https://deepseek.ai/blog/reasoning-era-gemini-3-deepseek" />
        
        <meta property="og:title" content="The Reasoning Era: Google Gemini 3 vs DeepSeek Deep Thinking AI" />
        <meta property="og:description" content="Discover how Gemini 3 and DeepSeek are shifting AI from pattern matching to true reasoning with System 2 thinking capabilities." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://deepseek.ai/blog/reasoning-era-gemini-3-deepseek" />
        <meta property="og:image" content="https://deepseek.ai/og-reasoning-era.png" />
        <meta property="article:published_time" content="2025-12-07" />
        <meta property="article:author" content="DeepSeek AI Fan Site" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="The Reasoning Era: Google Gemini 3 vs DeepSeek" />
        <meta name="twitter:description" content="AI stops guessing and starts thinking. Deep dive into the System 2 revolution." />
        <meta name="twitter:image" content="https://deepseek.ai/og-reasoning-era.png" />
        
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Head>

      <article className="min-h-screen pt-20 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <button onClick={() => navigate("/")} className="hover:text-primary transition-colors">Home</button>
            <ChevronRight className="h-4 w-4" />
            <button onClick={() => navigate("/blog")} className="hover:text-primary transition-colors">Blog</button>
            <ChevronRight className="h-4 w-4" />
            <span className="text-foreground">Reasoning Era</span>
          </nav>

          {/* Back Button */}
          <Button variant="ghost" onClick={() => navigate("/blog")} className="mb-6 -ml-4">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blog
          </Button>

          {/* Hero Image */}
          <div className="relative rounded-2xl overflow-hidden mb-8 shadow-2xl">
            <img 
              src={heroImage} 
              alt="The Reasoning Era: Google Gemini 3 and DeepSeek AI breakthrough" 
              className="w-full h-64 md:h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="px-3 py-1 bg-blue-600 text-white rounded-full text-xs font-medium">AI Technology</span>
                <span className="px-3 py-1 bg-purple-600 text-white rounded-full text-xs font-medium">Reasoning AI</span>
                <span className="px-3 py-1 bg-cyan-600 text-white rounded-full text-xs font-medium">Deep Thinking</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-bold text-white leading-tight">
                The Reasoning Era Begins: A Deep Dive into Google Gemini 3 and DeepSeek's Quantum Leap
              </h1>
            </div>
          </div>

          {/* Article Meta */}
          <div className="flex flex-wrap items-center gap-4 mb-8 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <time dateTime="2025-12-07">December 7, 2025</time>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>15 min read</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span>DeepSeek AI Editorial Team</span>
            </div>
          </div>

          <NavigationButtons />

          {/* Key Takeaways */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-xl mb-10 shadow-lg">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Lightbulb className="h-5 w-5" /> Key Takeaways
            </h2>
            <ul className="space-y-2 text-blue-50">
              <li>• AI shifts from "System 1" (fast pattern matching) to "System 2" (deliberative reasoning)</li>
              <li>• Gemini 3 introduces true multimodal reasoning across video, audio, and text</li>
              <li>• DeepSeek achieves top-tier reasoning at 10x lower cost with open weights</li>
              <li>• Inference-Time Compute replaces Training-Time Compute as the key differentiator</li>
              <li>• Both models feature self-correction mechanisms that verify before responding</li>
            </ul>
          </div>

          {/* Table of Contents */}
          <div className="bg-white/80 backdrop-blur p-6 rounded-xl mb-10 border border-slate-200 shadow-sm">
            <h2 className="text-lg font-bold mb-4 text-foreground">Table of Contents</h2>
            <nav className="space-y-2 text-sm">
              <a href="#paradigm-shift" className="block text-primary hover:underline">1. The Paradigm Shift: From System 1 to System 2</a>
              <a href="#gemini-3" className="block text-primary hover:underline">2. Google Gemini 3: The Multimodal Brain</a>
              <a href="#deepseek" className="block text-primary hover:underline">3. DeepSeek: The Open Efficiency Monster</a>
              <a href="#comparison" className="block text-primary hover:underline">4. Comparative Analysis: Which Model Wins?</a>
              <a href="#implications" className="block text-primary hover:underline">5. The Implications for You</a>
              <a href="#conclusion" className="block text-primary hover:underline">6. Final Thoughts</a>
              <a href="#faq" className="block text-primary hover:underline">7. Frequently Asked Questions</a>
            </nav>
          </div>

          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-muted-foreground leading-relaxed">
              For the past two years, the AI narrative has been dominated by a single metric: <strong>scale</strong>. Bigger parameters, larger datasets, and massive training clusters. But as we move deeper into 2025, the narrative has shifted. We have hit the point of diminishing returns on raw size.
            </p>
            <p className="text-xl font-semibold text-foreground mt-6">
              The new frontier isn't about knowing more; it's about <em>thinking better</em>.
            </p>
            <p className="text-lg text-muted-foreground mt-4">
              With the simultaneous arrival of <strong>Google Gemini 3</strong> and the latest breakthroughs from <strong>DeepSeek</strong>, we are officially entering the era of "Deep Thinking" or "Reasoning" AI. These are not just chatbots that predict the next likely word; they are engines capable of planning, critiquing, and solving multi-step problems.
            </p>
            <p className="text-lg text-muted-foreground">
              Here is the comprehensive breakdown of this massive upgrade and why it changes everything.
            </p>
          </div>

          {/* Section 1: Paradigm Shift */}
          <section id="paradigm-shift" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-foreground flex items-center gap-3">
              <Brain className="h-8 w-8 text-blue-600" />
              The Paradigm Shift: From "System 1" to "System 2"
            </h2>
            
            <p className="text-lg text-muted-foreground mb-6">
              To understand why Gemini 3 and DeepSeek are such a big deal, we have to look at cognitive psychology.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-xl">
                <h3 className="text-xl font-bold text-amber-800 mb-3">System 1 (Traditional LLMs)</h3>
                <p className="text-amber-900 font-medium mb-2">Fast, instinctive, and automatic.</p>
                <p className="text-amber-800 text-sm">
                  Think of GPT-4 or Gemini 1.5 Pro. You ask a question, and it immediately generates an answer based on pattern matching. It is impressive, but prone to "hallucinations" because it doesn't stop to check its work.
                </p>
              </div>
              
              <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-xl">
                <h3 className="text-xl font-bold text-emerald-800 mb-3">System 2 (The New Models)</h3>
                <p className="text-emerald-900 font-medium mb-2">Slow, deliberative, and logical.</p>
                <p className="text-emerald-800 text-sm">
                  This is what Gemini 3 and DeepSeek are introducing. When asked a complex math problem or a coding architecture question, these models <em>pause</em>. They generate internal "chains of thought," explore different paths, reject incorrect logic, and then deliver the final answer.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white p-6 rounded-xl">
              <p className="text-lg font-medium">
                This shift from <strong>"Training-Time Compute"</strong> (learning facts) to <strong>"Inference-Time Compute"</strong> (spending energy to think live) is the defining feature of this generation.
              </p>
            </div>
          </section>

          {/* Section 2: Google Gemini 3 */}
          <section id="gemini-3" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-foreground flex items-center gap-3">
              <Zap className="h-8 w-8 text-purple-600" />
              Google Gemini 3: The Multimodal Brain
            </h2>
            
            <p className="text-lg text-muted-foreground mb-6">
              Google has been under immense pressure to reclaim the throne from OpenAI and Anthropic. Gemini 3 appears to be that reclamation.
            </p>

            <div className="space-y-6">
              <div className="bg-white border border-slate-200 p-6 rounded-xl shadow-sm">
                <h3 className="text-xl font-bold text-foreground mb-3">1. True Multimodal Reasoning</h3>
                <p className="text-muted-foreground">
                  While previous models could "see" images, Gemini 3 can <strong>"reason"</strong> across them. You can upload a 20-minute video of a manufacturing process, and Gemini 3 can not only transcribe it but identify safety violations, suggest efficiency improvements, and output the timestamped logic for its conclusions. It connects visual data with textual logic seamlessly.
                </p>
              </div>

              <div className="bg-white border border-slate-200 p-6 rounded-xl shadow-sm">
                <h3 className="text-xl font-bold text-foreground mb-3">2. Self-Correction Mechanisms</h3>
                <p className="text-muted-foreground">
                  Gemini 3 introduces a recursive checking loop. In demos, when the model makes a coding error, it recognizes the syntax failure internally <em>before</em> showing the user the result, rewrites the code, and presents the clean version. This mimics a human developer's workflow: <code className="bg-slate-100 px-2 py-1 rounded text-sm">Draft → Test → Fix → Ship</code>.
                </p>
              </div>

              <div className="bg-white border border-slate-200 p-6 rounded-xl shadow-sm">
                <h3 className="text-xl font-bold text-foreground mb-3">3. Integration with the "Real World"</h3>
                <p className="text-muted-foreground">
                  Unlike a chatbot trapped in a window, Gemini 3 is designed as an <strong>"Agent."</strong> Because its reasoning capabilities are higher, Google is granting it more permission to interact with other apps—booking flights, managing calendars, and executing complex SQL queries within BigQuery without needing a human to double-check every step.
                </p>
              </div>
            </div>
          </section>

          {/* Section 3: DeepSeek */}
          <section id="deepseek" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-foreground flex items-center gap-3">
              <Code className="h-8 w-8 text-cyan-600" />
              DeepSeek: The Open Efficiency Monster
            </h2>
            
            <p className="text-lg text-muted-foreground mb-6">
              While Google aims for the enterprise consumer, DeepSeek (originating from China's premier AI research labs) is revolutionizing the developer and open-weights landscape.
            </p>

            <div className="space-y-6">
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-cyan-800 mb-3">1. Democratizing "Deep Thinking"</h3>
                <p className="text-cyan-900">
                  DeepSeek's major achievement is <strong>efficiency</strong>. They have managed to achieve reasoning capabilities comparable to top-tier proprietary models, but with a significantly smaller parameter footprint. This means "Deep Thinking" is no longer reserved for massive data centers; it can arguably run on high-end consumer hardware or affordable cloud instances.
                </p>
              </div>

              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-cyan-800 mb-3">2. The Coding Specialist</h3>
                <p className="text-cyan-900">
                  DeepSeek has laser-focused its training data on code and mathematics. Developers are reporting that the new DeepSeek models don't just complete functions; they <strong>understand system architecture</strong>. If you ask it to build a chat app, it doesn't just write the UI; it plans the database schema, the WebSocket connections, and the security protocols, explaining <em>why</em> it chose those specific technologies.
                </p>
              </div>

              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-cyan-800 mb-3">3. Breaking the Monopoly</h3>
                <p className="text-cyan-900">
                  The existence of DeepSeek proves that "Reasoning AI" won't be a moat exclusive to Silicon Valley giants. It provides a viable alternative for companies that want to run powerful reasoning models <strong>on-premise (locally)</strong> for data privacy reasons, rather than sending data to Google or OpenAI.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4: Comparison */}
          <section id="comparison" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-foreground flex items-center gap-3">
              <Scale className="h-8 w-8 text-indigo-600" />
              Comparative Analysis: Which Model Wins?
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                <thead>
                  <tr className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
                    <th className="p-4 text-left font-bold">Feature</th>
                    <th className="p-4 text-left font-bold">Google Gemini 3</th>
                    <th className="p-4 text-left font-bold">DeepSeek Models</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  <tr className="hover:bg-slate-50">
                    <td className="p-4 font-medium text-foreground">Primary Strength</td>
                    <td className="p-4 text-muted-foreground">General Purpose, Multimodal (Video/Audio)</td>
                    <td className="p-4 text-muted-foreground">Coding, Math, Logic</td>
                  </tr>
                  <tr className="hover:bg-slate-50">
                    <td className="p-4 font-medium text-foreground">Accessibility</td>
                    <td className="p-4 text-muted-foreground">Cloud API / Google Ecosystem</td>
                    <td className="p-4 text-muted-foreground">Open Weights / API</td>
                  </tr>
                  <tr className="hover:bg-slate-50">
                    <td className="p-4 font-medium text-foreground">Speed</td>
                    <td className="p-4 text-muted-foreground">Variable (Slows for complex tasks)</td>
                    <td className="p-4 text-muted-foreground">Highly Optimized / Efficient</td>
                  </tr>
                  <tr className="hover:bg-slate-50">
                    <td className="p-4 font-medium text-foreground">Best Use Case</td>
                    <td className="p-4 text-muted-foreground">Enterprise workflow, creative analysis, daily assistance</td>
                    <td className="p-4 text-muted-foreground">Software engineering, data science, local deployment</td>
                  </tr>
                  <tr className="hover:bg-slate-50">
                    <td className="p-4 font-medium text-foreground">Cost</td>
                    <td className="p-4 text-muted-foreground">Premium Enterprise Pricing</td>
                    <td className="p-4 text-cyan-600 font-medium">Low cost (often 10x cheaper per token)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 5: Implications */}
          <section id="implications" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-foreground flex items-center gap-3">
              <Building2 className="h-8 w-8 text-orange-600" />
              The Implications for You
            </h2>
            
            <p className="text-lg text-muted-foreground mb-6">
              This upgrade isn't just about better chatbots. It changes how we work.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white border border-slate-200 p-6 rounded-xl shadow-sm">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Code className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">For Developers</h3>
                <p className="text-muted-foreground text-sm">
                  The role shifts from "writing code" to "reviewing architecture." With DeepSeek and Gemini 3, you become the manager of an AI that does the heavy lifting.
                </p>
              </div>

              <div className="bg-white border border-slate-200 p-6 rounded-xl shadow-sm">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <Building2 className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">For Businesses</h3>
                <p className="text-muted-foreground text-sm">
                  The error rate of AI has historically been the barrier to adoption. With self-correcting reasoning models, we can finally trust AI with mission-critical tasks where accuracy is paramount.
                </p>
              </div>

              <div className="bg-white border border-slate-200 p-6 rounded-xl shadow-sm">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Timer className="h-6 w-6 text-orange-600" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">The "Slow" AI Acceptance</h3>
                <p className="text-muted-foreground text-sm">
                  Users will need to get used to the idea that a good answer takes time. We are trading milliseconds of latency for massive gains in IQ.
                </p>
              </div>
            </div>
          </section>

          {/* Conclusion */}
          <section id="conclusion" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-foreground">Final Thoughts</h2>
            
            <div className="bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 text-white p-8 rounded-2xl">
              <p className="text-xl leading-relaxed mb-6">
                The release of <strong>Google Gemini 3</strong> and the <strong>DeepSeek</strong> updates marks the moment AI stopped guessing and started <em>thinking</em>.
              </p>
              <p className="text-lg leading-relaxed mb-6 text-blue-100">
                We are no longer looking at a search engine that talks; we are looking at a <strong>reasoning engine that solves</strong>. Whether you choose the polished ecosystem of Google or the raw, efficient power of DeepSeek, one thing is certain: the intelligence ceiling has just been shattered.
              </p>
              <p className="text-2xl font-bold text-cyan-300">
                What will you build now that your AI can truly think?
              </p>
            </div>
          </section>

          {/* FAQ Section */}
          <section id="faq" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-foreground">Frequently Asked Questions</h2>
            
            <div className="space-y-4">
              <div className="bg-white border border-slate-200 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-foreground mb-2">What is the difference between System 1 and System 2 AI?</h3>
                <p className="text-muted-foreground">
                  System 1 AI (traditional LLMs like GPT-4) provides fast, instinctive responses based on pattern matching. System 2 AI (Gemini 3, DeepSeek) uses slow, deliberative reasoning with internal chains of thought, self-checking, and logical verification before delivering answers.
                </p>
              </div>

              <div className="bg-white border border-slate-200 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-foreground mb-2">How does DeepSeek compare to Google Gemini 3?</h3>
                <p className="text-muted-foreground">
                  DeepSeek focuses on efficiency, coding, and mathematics with open-weights accessibility. Google Gemini 3 excels at multimodal reasoning across video/audio with deep enterprise integration. DeepSeek is often 10x cheaper per token while Gemini 3 offers premium enterprise features.
                </p>
              </div>

              <div className="bg-white border border-slate-200 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-foreground mb-2">What is Inference-Time Compute?</h3>
                <p className="text-muted-foreground">
                  Inference-Time Compute refers to the computational energy spent thinking about a problem live, rather than relying solely on pre-trained knowledge. This allows AI models to pause, explore different paths, and verify their reasoning before responding.
                </p>
              </div>

              <div className="bg-white border border-slate-200 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-foreground mb-2">Can DeepSeek models run on consumer hardware?</h3>
                <p className="text-muted-foreground">
                  Yes, DeepSeek's major achievement is efficiency. They've achieved reasoning capabilities comparable to top-tier proprietary models with a smaller parameter footprint, making it possible to run on high-end consumer hardware or affordable cloud instances.
                </p>
              </div>

              <div className="bg-white border border-slate-200 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-foreground mb-2">Which model should I choose for software development?</h3>
                <p className="text-muted-foreground">
                  For software development, DeepSeek is often the better choice due to its laser focus on code and mathematics, open-weights accessibility for local deployment, and significantly lower cost per token. It understands system architecture and can plan complete applications.
                </p>
              </div>
            </div>
          </section>

          {/* Citation */}
          <div className="bg-slate-100 p-6 rounded-xl mb-10 border border-slate-200">
            <h3 className="text-sm font-bold text-foreground mb-2">Cite this article:</h3>
            <p className="text-sm text-muted-foreground font-mono">
              DeepSeek AI Fan Site. "The Reasoning Era Begins: A Deep Dive into Google Gemini 3 and DeepSeek's Quantum Leap." December 7, 2025. https://deepseek.ai/blog/reasoning-era-gemini-3-deepseek
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 mb-10">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              onClick={() => window.open("https://chromewebstore.google.com/detail/ai-sidebar-with-deepseek/inhcgfpbfdjbjogdfjbclgolkmhnooop", "_blank")}
            >
              <Chrome className="mr-2 h-5 w-5" />
              Add to Chrome - It's Free
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => setIsDialogOpen(true)}
            >
              Join the AI Community
            </Button>
          </div>

          <WaitlistDialog isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)} />
        </div>
      </article>
    </>
  );
};

export default ReasoningEraGeminiDeepseek;
